import React, { useEffect, useRef } from "react";
import { useAlert } from "react-alert";
import {
  manageRequest,
  fetchLogsFromServer,
  returnedsurflogStatus,
  translateJsonFileName,
  translateBinFileName,
  calculateTotalProgress,
} from "../../../core/tools.js";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import LeaderLine from "react-leader-line";
import { useStateIfMounted } from "use-state-if-mounted";
import mqtt_topics from "./../../../mqtt/mqtt_topics";
import useCheckMobileScreen from "../readingView/useCheckMobileScreen.jsx";
import { confirmAlert } from "react-confirm-alert";
import {
  surfLogParsingEngine,
  extractLogHeaders,
  filterLogColumns,
  getHeaderLineIndexes,
  loadHiddenLogColumns,
  saveHiddenLogColumns,
} from "./../../../core/dataParser.js";
import DownloadExcel from "../../../core/DownloadExcel.js";
const appparameters = require("../../../core/common.jsx");

/*
 * Column picker shown between parsing and the Excel download.
 *
 * Rendered by react-confirm-alert's customUI, which lives in its OWN
 * ReactDOM root on document.body -- NOT a portal. So: hooks are fine here
 * (this is a real function component with its own fiber), but app context is
 * not -- never call useAlert() or useHistory() inside this tree. Hand the
 * selection back to handleExecute and toast from there.
 *
 * Declared at module scope so it isn't redeclared on every render of Logs.
 */
const LogColumnPicker = ({
  allHeaders,
  initiallyHidden,
  siteId,
  logType,
  compact,
  onConfirm,
  onCancel,
}) => {
  const [state, setState] = useStateIfMounted({
    hidden: new Set(initiallyHidden || []),
    filter: "",
  });

  const needle = state.filter.trim().toLowerCase();
  const shown =
    needle === ""
      ? allHeaders
      : allHeaders.filter((h) => h.toLowerCase().indexOf(needle) !== -1);

  const selectedCount = allHeaders.length - state.hidden.size;
  const canConfirm = selectedCount > 0;

  const toggle = (name) => {
    const next = new Set(state.hidden);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setState({ ...state, hidden: next });
  };
  // Select/clear act on the CURRENT FILTER, so "filter Aux -> Clear all shown"
  // is a one-gesture bulk hide.
  const showShown = () => {
    const next = new Set(state.hidden);
    shown.forEach((h) => next.delete(h));
    setState({ ...state, hidden: next });
  };
  const hideShown = () => {
    const next = new Set(state.hidden);
    shown.forEach((h) => next.add(h));
    setState({ ...state, hidden: next });
  };


  // Stable ids: position in allHeaders, so a name keeps its id while filtering.
  // Never derived from the name itself -- names contain spaces, slashes and
  // parens ("Date/Time", "Grid V(RN)") that are unsafe in an id.
  const idOf = new Map(allHeaders.map((n, i) => [n, "logcol-" + i]));

  return (
    // Plain Bootstrap .alert panel, matching the app's other dialogs
    // (navbar.js, tools.jsx). The flex column and maxHeight are structural, not
    // decorative: the overlay is position:fixed with align-items:center and the
    // library sets body{overflow:hidden}, so a dialog taller than the viewport
    // pushes its own title off-screen with no way to scroll back to it.
    <div
      className="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: compact ? "85vh" : "80vh",
        width: "92vw",
        maxWidth: compact ? "440px" : "620px",
        margin: 0, // Bootstrap .alert adds margin-bottom:1rem
        padding: compact ? "16px" : "20px",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 20px 75px rgba(0,0,0,0.13)",
        textAlign: "left",
      }}
    >
      <h1
        className="alert__title"
        style={{
          fontSize: compact ? "18px" : "21px",
          margin: "0 0 4px",
          fontWeight: 600,
        }}
      >
        Choose Excel columns
      </h1>
      <p
        className="alert__body text-muted"
        style={{ fontSize: "12.5px", marginBottom: "10px", lineHeight: 1.4 }}
        title="Columns that share a name are shown or hidden together."
      >
        {siteId} &middot; {logType} log &middot; unticked columns are left out of
        the .xlsx, and remembered for this device.
      </p>

      {/* Deliberately a plain .form-control: Bootstrap already overrides every
          visible property of app.css's global input{} rule, and the few that do
          leak through (margin-bottom, text-shadow, rest-state box-shadow) leak
          into every other .form-control in this app too, so this matches them.
          An inline boxShadow:"none" here would also beat .form-control:focus
          and destroy the focus ring. */}
      <input
        className="form-control form-control-sm"
        placeholder="Filter columns..."
        value={state.filter}
        // Not on mobile: it opens the keyboard, which eats the very viewport
        // height the maxHeight cap is protecting.
        autoFocus={!compact}
        onChange={(e) => setState({ ...state, filter: e.target.value })}
      />

      <div
        className="d-flex justify-content-between align-items-center mb-2"
        style={{ fontSize: "12px" }}
      >
        <div>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={showShown}
          >
            Select all{needle ? " shown" : ""}
          </button>
          <span className="mx-2 text-muted">|</span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={hideShown}
          >
            Clear all{needle ? " shown" : ""}
          </button>
        </div>
        <span className={canConfirm ? "text-muted" : "text-danger"}>
          {selectedCount} of {allHeaders.length}
          {needle !== "" ? " (" + shown.length + " shown)" : ""}
        </span>
      </div>

      {/* The only scroll region. minHeight:0 is required -- flex items default
          to min-height:auto and refuse to shrink below their content, which
          would silently defeat the maxHeight cap above. */}
      <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        {shown.length === 0 ? (
          <div className="text-muted" style={{ padding: "12px 0" }}>
            No column matches "{state.filter}".
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: compact
                ? "1fr"
                : "repeat(auto-fill, minmax(240px, 1fr))",
              columnGap: "12px",
            }}
          >
            {shown.map((name) => (
              // custom-control, exactly as editsite.js and checkbox.jsx use it.
              // .custom-control-input is opacity:0 + position:absolute, so
              // app.css's global input{width:100%;padding:10px;background:...}
              // lands on an invisible, out-of-flow element rather than turning
              // every checkbox into a full-width dark bar. The visible box is
              // .custom-control-label::before -- a pseudo-element that rule
              // cannot reach. The id/htmlFor pair is mandatory here: the label
              // IS the control, and it only toggles through that association.
              <div className="custom-control custom-checkbox" key={name}>
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={idOf.get(name)}
                  checked={!state.hidden.has(name)}
                  onChange={() => toggle(name)}
                />
                <label
                  className="custom-control-label"
                  htmlFor={idOf.get(name)}
                  title={name}
                  style={{ fontSize: "13.5px", cursor: "pointer" }}
                >
                  {name}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 d-flex align-items-center">
        <button
          className="btn btn-primary"
          disabled={!canConfirm}
          onClick={() => onConfirm(Array.from(state.hidden))}
        >
          Create Excel
        </button>
        &nbsp;
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        {!canConfirm && (
          <span className="text-danger ml-3" style={{ fontSize: "12px" }}>
            Select at least one column.
          </span>
        )}
        <button
          className="btn btn-link btn-sm"
          style={{ marginLeft: "auto" }}
          onClick={() => setState({ ...state, hidden: new Set() })}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export const Logs = (props) => {
  const mac = mqtt_topics.choosenmac;
  const history = useHistory();
  const location = useLocation();
  const alert = useAlert();
  const leaderLinesRef = useRef({});
  let mobilescreen = useCheckMobileScreen();
  function handlesize(value) {
    return mobilescreen ? value / 2 : value;
  }
  const [iniDate, setIniDate] = useStateIfMounted(
    new Date().toISOString().split("T")[0]
  );
  const [startDate, setStartDate] = useStateIfMounted(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useStateIfMounted(
    new Date().toISOString().split("T")[0]
  );
  const [retrieveStatus, setRetrieveStatus] = useStateIfMounted();
  const [uploading, setuploading] = useStateIfMounted(0);
  const [downloading, setdownloading] = useStateIfMounted(0);
  const [listoflogfiles, setListoflogfiles] = useStateIfMounted(null);
  const [jsonFilesContent, setJsonFilesContent] = useStateIfMounted(null);
  const [dataFilesContent, setDataFilesContent] = useStateIfMounted(null);
  const [lastmqttmessagetime, setlastmqttmessagetime] = useStateIfMounted(
    new Date().getTime()
  );
  const [requestCheck, setRequestCheck] = useStateIfMounted(false);
  const [startButton, setStartButton] = useStateIfMounted(false);
  const [surflogStatus, setSurflogStatus] = useStateIfMounted(
    returnedsurflogStatus
  );
  const invisbleLogsDesignWhileRetrieve10DaysFaults = useRef(false);

  const Spacer = ({ size }) => (
    <div style={{ width: size, display: "inline-block" }}></div>
  );
  let vectorSize = mobilescreen ? 2 : 4;
  let logstopic = mqtt_topics.fixed_topic_part + mac + "/LogRetrieve";
  const [selectedOption, setSelectedOption] = useStateIfMounted("time");
  const [shouldRequestLogs, setShouldRequestLogs] = useStateIfMounted(false); // New flag to control log retrieval

  useEffect(() => {
    if (location.goingto === "fl") {
      invisbleLogsDesignWhileRetrieve10DaysFaults.current = false;
      setSelectedOption("fault");
      setStartDate(getDateMinussetofDays(10));
      setShouldRequestLogs(true); // Trigger request log retrieval after state updates
    }
  }, [location.goingto]);

  // Effect that runs only after state has updated
  useEffect(() => {
    if (shouldRequestLogs && selectedOption === "fault" && startDate) {
      requestlogretrieve();
      setShouldRequestLogs(false); // Reset flag to prevent re-triggering
      invisbleLogsDesignWhileRetrieve10DaysFaults.current = true;
    }
  }, [selectedOption, startDate, shouldRequestLogs]);

  const getDateMinussetofDays = (days) => {
    const today = new Date();
    today.setDate(today.getDate() - days); // Subtract 10 days
    return today.toISOString().split("T")[0]; // Format to YYYY-MM-DD
  };
  useEffect(() => {
    const interval = setInterval(() => {
      keepcheckforlogretrievetimeout();
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, [uploading, lastmqttmessagetime]);

  useEffect(() => {
    if (
      dataFilesContent !== null &&
      jsonFilesContent !== null &&
      listoflogfiles !== null
    ) {
      console.log("Checking conditions...");
      console.log("dataFilesContent:", Object.keys(dataFilesContent).length);
      console.log("jsonFilesContent:", Object.keys(jsonFilesContent).length);
      console.log("listoflogfiles:", listoflogfiles);
      if (
        uploading != 1 &&
        downloading != 1 &&
        Object.keys(dataFilesContent).length +
          Object.keys(jsonFilesContent).length !=
          listoflogfiles
      ) {
        console.log("Retrieved data is incomplete, please try again.");
        alert.show("Retrieved data is incomplete, please try again.", {
          type: "error",
          timeout: 0,
          position: "middle",
        });
        finishedretrievinglogs();
      }
    }
    if (
      dataFilesContent !== null &&
      jsonFilesContent !== null &&
      listoflogfiles !== null &&
      Object.keys(dataFilesContent).length +
        Object.keys(jsonFilesContent).length ==
        listoflogfiles
    ) {
      //setuploading(2);
      //console.log(dataFilesContent);
      //console.log(jsonFilesContent);
      //console.log("lengthvalues matches correct and ready to parse");
      handleExecute(jsonFilesContent, dataFilesContent);
    } else {
      //console.log("dataFilesContent:", Object.keys(dataFilesContent).length);
      //console.log("jsonFilesContent:", Object.keys(jsonFilesContent).length);
      //console.log("listoflogfiles:", listoflogfiles);
    }
  }, [dataFilesContent, jsonFilesContent]);

  const handleBeforeUnload = (event) => {
    const message =
      "Leaving this page will result in losing firmware upgrade progress. Are you sure?";
    event.returnValue = message; // Standard for most browsers
    return message; // For some older browsers
  };

  /* useEffect(() => {
    // Function to handle beforeunload event
    const handleBeforeUnload = (event) => {
      if (uploading === 1 || downloading === 1) {
        event.preventDefault();
        event.returnValue = ""; // Standard way to trigger the confirmation dialog
      }
    };

    // Function to handle navigation
    const handleNavigation = (event) => {
      if (uploading === 1 || downloading === 1) {
        event.preventDefault();
        event.returnValue = ""; // Standard way to trigger the confirmation dialog
      }
    };

    // Add beforeunload event listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Add event listener to handle navigation using history
    const unblock = history.block((location, action) => {
      if (uploading === 1 || downloading === 1) {
        if (action === "POP") {
          // 'POP' action refers to back/forward navigation
          return "You have unsaved changes. Are you sure you want to leave?";
        }
        // For other actions like PUSH (navigation via links), Prompt will handle
        finishedretrievinglogs();
      }
      return true;
    });

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock(); // Unblock navigation
    };
  }, [uploading, downloading, history]);*/

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (uploading === 1 || downloading === 1) {
        const confirmLeave = window.confirm(
          "You have uploads/downloads in progress. Do you really want to leave?"
        );

        if (confirmLeave) {
          // ✅ Run your custom cleanup code here
          finishedretrievinglogs();

          // unblock navigation so it can proceed
          unblock(); // important: remove the block

          // return true to allow the navigation
          return true;
        } else {
          // return false to block navigation
          return false;
        }
      }
      // no uploads/downloads in progress → allow navigation
      return true;
    });

    return () => unblock();
  }, [uploading, downloading, history]);

  useEffect(
    () => () => {
      if (uploading == 1) {
        sendAbortRequest();
      } //else if (downloading == 1) {
      finishedretrievinglogs();
      //}
    },
    []
  );

  useEffect(() => {
    createAndUpdateVectors(
      "surf_icon",
      "server_icon",
      uploading == 1,
      "arrow",
      "right",
      "left",
      retrieveStatus,
      "",
      ""
      //[15, -20]
    );
    createAndUpdateVectors(
      "server_icon",
      "pc_icon",
      downloading == 1,
      "arrow",
      "right",
      "left",
      retrieveStatus,
      "",
      ""
      //[10, -20]
    );
    //createAndUpdateCirclularVectors("pc_icon", downloading == 2);
  }, [uploading, downloading, retrieveStatus]); //, uploadingProgress, downloadingProgress

  /*
   * Opens the column picker and resolves with the user's choice:
   *   string[] of HIDDEN column names on confirm ([] is a valid confirm --
   *   nothing hidden), or null on cancel. That's why the cancel sentinel is
   *   null and not [] -- `=== null` has to be unambiguous.
   */
  const askForColumns = (allHeaders, initiallyHidden) =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (v) => {
        if (!settled) {
          settled = true;
          resolve(v);
        }
      };
      confirmAlert({
        closeOnClickOutside: false, // a tall scroller makes stray clicks likely
        closeOnEscape: true,
        // SAFETY NET: Escape calls the library's close() directly, bypassing
        // our buttons. close() always routes through afterClose, so this is
        // the only thing that resolves the promise on that path. Without it
        // handleExecute awaits forever and the page wedges until reload.
        afterClose: () => finish(null),
        customUI: ({ onClose }) => (
          <LogColumnPicker
            allHeaders={allHeaders}
            initiallyHidden={initiallyHidden}
            siteId={mqtt_topics.choosenid}
            logType={selectedOption}
            compact={mobilescreen}
            onConfirm={(hidden) => {
              finish(hidden); // sets `settled` before onClose, so the
              onClose(); // afterClose -> finish(null) below no-ops
            }}
            onCancel={() => {
              finish(null);
              onClose();
            }}
          />
        ),
      });
    });

  const handleExecute = async (data, json) => {
    try {
      // Set initial status
      //console.log("set Extracting and parsing data ...");
      setRetrieveStatus("Extracting and parsing data ...");

      // Let React process the update before proceeding
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Execute the async operation
      const output = await surfLogParsingEngine(data, json);

      if (output.error) {
        alert.show("Failed to parse data error:" + output.error, {
          type: "error",
          timeout: 0, // Alert stays visible until dismissed
          position: "middle",
        });
      } else {
        // Update status to extraction success
        //console.log("set Extracting Successfully");
        setRetrieveStatus("Extracting Successfully");

        // Let React process the update before proceeding
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (output.value !== "" && output.value !== undefined) {
          const allHeaders = extractLogHeaders(output.value);
          let dataToExport = output.value;

          if (allHeaders.length > 0) {
            setRetrieveStatus("Waiting for column selection ...");

            // Let React process the update before proceeding
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Drop stale names (firmware may have removed columns) so they
            // don't linger in the saved set forever.
            const savedHidden = loadHiddenLogColumns(mac, selectedOption).filter(
              (name) => allHeaders.indexOf(name) !== -1
            );

            const chosenHidden = await askForColumns(allHeaders, savedHidden);

            if (chosenHidden === null) {
              // Cancel / Escape: no file. finishedretrievinglogs() still runs,
              // it moved into the finally below.
              alert.show("Excel export cancelled.", {
                type: "info",
                position: "middle",
              });
              return;
            }

            saveHiddenLogColumns(mac, selectedOption, chosenHidden);

            const filtered = filterLogColumns(output.value, chosenHidden);
            if (filtered.trim() === "") {
              // Only reachable when stale prefs meet a changed column set.
              // Never ship an empty file.
              alert.show(
                "None of the selected columns exist in this data set; exporting all columns instead.",
                { type: "info", timeout: 0, position: "middle" }
              );
            } else {
              dataToExport = filtered;
            }
          }

          // Update status to creating Excel file
          //console.log("set Creating Excel File to download ...");
          setRetrieveStatus("Creating Excel File to download ...");

          // Let React process the update before proceeding
          await new Promise((resolve) => setTimeout(resolve, 100));

          await DownloadExcel(
            dataToExport,
            {
              dataType: selectedOption + "log",
              siteId: mqtt_topics.choosenid,
              from: formatDate(new Date(startDate)),
              to: formatDate(new Date(endDate)),
            },
            getHeaderLineIndexes(dataToExport)
          );
        } else {
          alert.show("Failed to parse data", {
            type: "error",
            timeout: 0,
            position: "middle",
          }); // Alervvt stays visible until dismissed// Alert stays visible until dismissed
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      // In a finally so the cancel path's early return still resets the page
      // (and so a throw above no longer skips it, which it used to).
      finishedretrievinglogs();
    }
  };
  const startFetchingDirectoriesOnServer = async () => {
    try {
      setdownloading(1);
      await new Promise((resolve) => setTimeout(resolve, 50)); // Small delay
      const list_of_files_to_retrieve = await fetchLogsFromServer(
        "listoflogfiles.txt",
        mac
      );

      if (list_of_files_to_retrieve) {
        let filesArray = list_of_files_to_retrieve.split("\n");
        setListoflogfiles(filesArray.length - 1);

        for (
          let fileindex = 0;
          fileindex < filesArray.length - 1;
          fileindex++
        ) {
          let data = await fetchLogsFromServer(filesArray[fileindex], mac);

          if (data) {
            if (
              filesArray[fileindex]
                .slice(((filesArray[fileindex].lastIndexOf(".") - 1) >>> 0) + 2)
                .toLowerCase() === "jsn"
            ) {
              setJsonFilesContent((prevJsonFilesContent) => ({
                ...prevJsonFilesContent,
                [translateJsonFileName(filesArray[fileindex])]: data,
              }));
            } else {
              setDataFilesContent((prevDataFilesContent) => ({
                ...prevDataFilesContent,
                [translateBinFileName(filesArray[fileindex])]: data.data,
              }));
            }
          }
          // Update status after each file retrieval
          setRetrieveStatus(
            `Downloading file ${fileindex + 1} of ${filesArray.length - 1}`
          );
          await new Promise((resolve) => setTimeout(resolve, 50)); // Small delay
        }
      } else {
        alert.show("No datalog Found", {
          type: "info",
          timeout: 0,
          position: "middle",
        });
        finishedretrievinglogs();
      }
    } catch (error) {
      console.error("Error fetching directories:", error);
      alert.show("An error occurred while retrieving logs.", {
        type: "error",
        timeout: 0,
        position: "middle",
      });
    } finally {
      //setdownloading(0);
      finishedretrievinglogs();
    }
  };

  const finishedretrievinglogs = async () => {
    invisbleLogsDesignWhileRetrieve10DaysFaults.current = false;
    window.removeEventListener("beforeunload", handleBeforeUnload);
    setStartButton(false);
    setuploading(0);
    setdownloading(0);
    setListoflogfiles(null);
    setJsonFilesContent(null);
    setDataFilesContent(null);
    setRetrieveStatus("");
    setRequestCheck(false);
    manageRequest(
      "delete",
      "Get Log",
      localStorage.getItem("token"),
      localStorage.getItem("username"),
      mqtt_topics.choosenmac
    );
    unsubscribeToSurf();
    try {
      const response = await fetch(
        appparameters.backend_app_url + `/frontend/removeSurfLogs`,
        {
          method: "post",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userData: {
              tag: mac,
              token: localStorage.getItem("token"),
              username: localStorage.getItem("username"),
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.content) {
        return data.content;
      } else {
        console.error("No content in response");
        return null;
      }
    } catch (error) {
      console.error("Error fetching file content:", error);
      return null;
    }
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  function subscribeToSurf() {
    location.mqttClient.subscribe(logstopic);
    location.mqttClient.on("message", handleMqttMessage);
  }
  function unsubscribeToSurf() {
    location.mqttClient.unsubscribe(logstopic);
    location.mqttClient.removeListener("message", handleMqttMessage);
  }

  function handleMqttMessage(topic, message) {
    if (topic == logstopic) {
      setlastmqttmessagetime(new Date().getTime());
      message = JSON.parse(message.toString());
      if (message.LogRetrieveStatus.includes(surflogStatus[3])) {
        //"Uploading Files Started"
        if (message.FileUploadProgress != undefined) {
          setRetrieveStatus(
            message.LogRetrieveStatus +
              " " +
              calculateTotalProgress(
                message.TotalFilesNumber,
                message.CurrentSendingFileIndex,
                message.FileUploadProgress
              ) +
              "%"
          );
          /*
                        message.CurrentSendingFileIndex +
              "/" +
              message.TotalFilesNumber +
              " " +
              message.FileUploadProgress +
          */
        } else {
          setRetrieveStatus(
            message.LogRetrieveStatus +
              " " +
              message.CurrentSendingFileIndex +
              "/" +
              message.TotalFilesNumber +
              " ..."
          );
        }
        setuploading(1);
      } else {
        if (
          message.LogRetrieveStatus == surflogStatus[5] ||
          message.LogRetrieveStatus == surflogStatus[6]
        ) {
          //failed upload
          finishedretrievinglogs();
          alert.show(message.LogRetrieveStatus, {
            type: "error",
            timeout: 0,
            position: "middle",
          });
        } else {
          //suceesfull upload
          setuploading(0);
          if (message.LogRetrieveStatus == surflogStatus[4]) {
            setRetrieveStatus("Downloading Files ...");
            startFetchingDirectoriesOnServer();
          } else {
            console.log(message.LogRetrieveStatus);
            console.log(invisbleLogsDesignWhileRetrieve10DaysFaults.current);
            if (
              (message.LogRetrieveStatus == "No Faults To View" ||
                message.LogRetrieveStatus == "No Log Files Found") &&
              invisbleLogsDesignWhileRetrieve10DaysFaults.current
            ) {
              message.LogRetrieveStatus =
                "No faults detected in the last 10 days";
            }
            alert.show(message.LogRetrieveStatus, {
              type: "info",
              timeout: 0,
              position: "middle",
            });
            finishedretrievinglogs();
          }
        }
        setRetrieveStatus(message.LogRetrieveStatus);
      }
    }
  }

  function keepcheckforlogretrievetimeout() {
    if (uploading == 1) {
      /* if (new Date().getTime() - lastmqttmessagetime > 240 * 1000) {
        //4 mins delay
        finishedretrievinglogs();
        alert.show(
          "Log retrieve failed; the timeout was reached. Please retry.",
          { type: "error", timeout: 0 }
        );
      }*/
    }
  }

  const requestlogretrieve = async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));
    //console.log("selectedOption:" + selectedOption);
    //console.log("local storage value:" + localStorage.getItem("fl_of_" + mac));
    if (
      selectedOption === "fault" &&
      localStorage.getItem("fl_of_" + mac) != "1"
    ) {
      return alert.show(
        mqtt_topics.choosenid +
          " Device is not compatible with fault retrieval. For more information, contact support.",
        {
          type: "info",
          position: "middle",
        }
      );
    }
    setRequestCheck(true);
    if (
      await manageRequest(
        "check",
        "Get Log",
        localStorage.getItem("token"),
        localStorage.getItem("username"),
        mqtt_topics.choosenmac
      )
    ) {
      setRequestCheck(false);
      manageRequest(
        "add",
        "Get Log",
        localStorage.getItem("token"),
        localStorage.getItem("username"),
        mqtt_topics.choosenmac
      );
      setStartButton(true);
      subscribeToSurf();
      location.mqttClient.publish(
        mqtt_topics.fixed_topic_part + mac + mqtt_topics.request_topic,
        JSON.parse(
          JSON.stringify(
            '{ "action": "Get Log","directory":"/frontend/uploadSASLogs" ,"type":"' +
              selectedOption +
              '","from":"' +
              formatDate(new Date(startDate)) +
              '","to":"' +
              formatDate(new Date(endDate)) +
              '","token":"' +
              localStorage.getItem("token") +
              '","username":"' +
              localStorage.getItem("username") +
              '"}'
          )
        )
      );
    } else {
      alert.show(
        mqtt_topics.choosenid + " is busy right now. Please try again later.",
        {
          type: "info",
          position: "middle",
        }
      );
    }
  };

  function abortlogretrieve() {
    if (uploading) {
      confirmAlert(abortlogoptions);
    } else {
      alert.show("No log progress detected to abort.", {
        type: "error",
        position: "middle",
      });
    }
  }

  function sendAbortRequest() {
    location.mqttClient.publish(
      mqtt_topics.fixed_topic_part + mac + mqtt_topics.request_topic,
      JSON.parse(JSON.stringify('{ "action": "Abort Get Log" }'))
    );
  }

  const abortlogoptions = {
    customUI: ({ onClose }) => {
      return (
        <div className="alert">
          <h1 className="alert__title">Abort Log Retrieval Confirmation</h1>
          <p className="alert__body">
            Are you sure you want to abort the log retrieval for{" "}
            {mqtt_topics.choosenid}?
          </p>
          <button
            onClick={() => {
              sendAbortRequest();
              //finishedretrievinglogs();
              onClose();
            }}
            className="btn btn-primary"
          >
            Procced
          </button>
          &nbsp;
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      );
    },
  };

  function formatDate(date) {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function addOneMonthToDate(date) {
    date = new Date(date);
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().split("T")[0];
  }

  function subtractOneMonthFromDate(date) {
    date = new Date(date);
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  }

  function createAndUpdateVectors(
    from,
    to,
    onload,
    path,
    startSocket,
    endSocket,
    startLabel,
    middleLabel,
    endLabel,
    offset,
    startSocketGravity
  ) {
    let startcolor = "#25b39e";
    let endclor = "lightsalmon "; //gold
    if (
      document.getElementById(from) != undefined &&
      document.getElementById(to) != undefined
    ) {
      let target = from + "_" + to;
      let oppositTarget = to + "_" + from;
      if (onload) {
        let options = {
          startPlugColor: startcolor,
          endPlugColor: endclor,
          gradient: true,
          dash: { animation: true },
          path: path,
          startSocket: startSocket,
          endSocket: endSocket,
          //endPlugSize: vectorEndPlugSize,
          size: vectorSize,
          startLabel: LeaderLine.captionLabel(startLabel, {
            color: startcolor,
            fontSize: mobilescreen ? "0.450em" : "0.750em",
            offset: offset,
          }),
          middleLabel: middleLabel,
          endLabel: LeaderLine.captionLabel(endLabel, {
            color: endclor,
          }),
          startSocketGravity: startSocketGravity,
          endPlug: "behind",
        };

        if (leaderLinesRef.current[target] != undefined) {
          try {
            leaderLinesRef.current[target].setOptions(options);
          } catch (Exception) {
            leaderLinesRef.current[target] = new LeaderLine(
              document.getElementById(from),
              document.getElementById(to),
              options
            );
            //check the opposit if found delete
            if (leaderLinesRef.current[oppositTarget] != undefined) {
              leaderLinesRef.current[oppositTarget].remove();
              delete leaderLinesRef.current[oppositTarget];
            }
          }
        } else {
          leaderLinesRef.current[target] = new LeaderLine(
            document.getElementById(from),
            document.getElementById(to),
            options
          );
          //check the opposit if found delete
          if (leaderLinesRef.current[oppositTarget] != undefined) {
            leaderLinesRef.current[oppositTarget].remove();
            delete leaderLinesRef.current[oppositTarget];
          }
        }
      } else {
        if (leaderLinesRef.current[target] != undefined) {
          try {
            leaderLinesRef.current[target].remove();
            delete leaderLinesRef.current[target];
          } catch (Exception) {}
        }
      }
    }
  }

  function createAndUpdateCirclularVectors(target, onload) {
    const img = document.getElementById(target);
    if (img) {
      if (onload) {
        const imgRect = img.getBoundingClientRect();

        // Define the points around the image
        const points = [
          { x: imgRect.left, y: imgRect.top },
          { x: imgRect.right, y: imgRect.top },
          { x: imgRect.right, y: imgRect.bottom },
          { x: imgRect.left, y: imgRect.bottom },
        ];

        // Draw lines between the points
        leaderLinesRef.current[target] = points.map((point, index) => {
          const nextPoint = points[(index + 1) % points.length];
          return new LeaderLine(
            LeaderLine.pointAnchor({
              element: document.body,
              x: point.x,
              y: point.y,
            }),
            LeaderLine.pointAnchor({
              element: document.body,
              x: nextPoint.x,
              y: nextPoint.y,
            }),
            {
              color: "lightsalmon",
              //size: 2,
              dash: { animation: true },
              endPlug: "behind",
            }
          );
        });
      } else {
        if (leaderLinesRef.current[target] != undefined) {
          try {
            leaderLinesRef.current[target].remove();
            delete leaderLinesRef.current[target];
          } catch (Exception) {}
        }
      }
    }
  }

  // Function to handle input event and prevent clear action
  const handleInput = (e, currentDate) => {
    if (!e.target.value && currentDate) {
      // Revert to previous date if clear button is clicked
      e.target.value = currentDate;
    }
  };
  //LogsPage
  return (
    <div>
      <div className="container">
        <div className="row loginrow">
          <div className="col">
            <div className=" d-flex justify-content-start align-items-center position-relative text-center w-100 text-white">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="close text-white"
                title="Back"
                style={{ zIndex: 1 }}
              >
                &#x3c;&#x3c;
              </button>
              <div className="text-center w-100">
                <strong>Retrieve Logs Page</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container  text-center">
        <div className="row  ">
          <div className="col">
            <img
              id="surf_icon"
              src={require("./../../../images/surflogoo.png")}
              style={{
                height: handlesize(80) + "px",
                width: handlesize(80) + "px",
              }}
            />
            <br />
            <strong>{mqtt_topics.choosenid}</strong>
          </div>

          <div className="col">
            <img
              id="server_icon"
              src={require("./../../../images/firmwareupgrade/cloud_server.png")}
              style={{
                height: handlesize(80) + "px",
                width: handlesize(65) + "px",
              }}
            />
          </div>

          <div className="col">
            <img
              id="pc_icon"
              src={require("./../../../images/firmwareupgrade/computer.png")}
              style={{
                height: handlesize(80) + "px",
                width: handlesize(60) + "px",
              }}
            />
          </div>
        </div>
        <br></br>
        {!invisbleLogsDesignWhileRetrieve10DaysFaults.current && (
          <div className="row  justify-content-start">
            <div className="col-sm-4">
              Start Date:
              <input
                type="date"
                style={{
                  width: "150px",
                  cursor: "pointer",
                  padding: "2px",
                  marginRight: "10px",
                  marginLeft: "10px",
                  border: "1px solid #ccc",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
                className=" btn-light btn-sm"
                value={startDate}
                min={subtractOneMonthFromDate(endDate)}
                max={addOneMonthToDate(endDate)}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={(e) => e.preventDefault()} // Disable keyboard input
                onInput={(e) => handleInput(e, iniDate)} // To handle the clear button
              />
            </div>
            <div className="col-sm-4">
              End Date:
              <input
                type="date"
                style={{
                  width: "150px",
                  cursor: "pointer",
                  padding: "2px",
                  marginRight: "10px",
                  marginLeft: "10px",
                  border: "1px solid #ccc",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
                className=" btn-light btn-sm"
                value={endDate}
                min={subtractOneMonthFromDate(startDate)}
                max={addOneMonthToDate(startDate)}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={(e) => e.preventDefault()} // Disable keyboard input
                onInput={(e) => handleInput(e, iniDate)} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear buttononInput={handleInput} // To handle the clear button
              />
            </div>
          </div>
        )}
        {!invisbleLogsDesignWhileRetrieve10DaysFaults.current && (
          <div className="row  row justify-content-center">
            <div className="col-3">
              <input
                type="radio"
                id="time"
                name="options"
                value="time"
                checked={selectedOption === "time"}
                onChange={handleOptionChange}
                disabled={startButton}
              />
              <label htmlFor="time">Time Log</label>
            </div>
            <div className="col-3">
              <input
                type="radio"
                id="event"
                name="options"
                value="event"
                checked={selectedOption === "event"}
                onChange={handleOptionChange}
                disabled={startButton}
              />
              <label htmlFor="event">Event Log</label>
            </div>
            <div className="col-3">
              <input
                type="radio"
                id="chart"
                name="options"
                value="chart"
                checked={selectedOption === "chart"}
                onChange={handleOptionChange}
                disabled={startButton}
              />
              <label htmlFor="chart">Chart Log</label>
            </div>
            <div className="col-3">
              <input
                type="radio"
                id="fault"
                name="options"
                value="fault"
                checked={selectedOption === "fault"}
                onChange={handleOptionChange}
                disabled={startButton}
              />
              <label htmlFor="fault">Fault Log</label>
            </div>
          </div>
        )}
        {invisbleLogsDesignWhileRetrieve10DaysFaults.current && (
          <div>
            <br />
            <hr></hr>
            <center>
              The faults from the last 10 days are being retrieved automatically
              whenever you enter the fault logs page.
            </center>
          </div>
        )}

        <br />
        <br />
        {!invisbleLogsDesignWhileRetrieve10DaysFaults.current && (
          <button
            className="btn btn-info"
            onClick={() => requestlogretrieve()}
            disabled={startButton}
            style={{ visibility: requestCheck ? "hidden" : "visible" }}
          >
            Start Retrieval
          </button>
        )}

        <div
          className="spinner-border text-primary"
          style={{ visibility: !requestCheck ? "hidden" : "visible" }}
        ></div>
        <Spacer size={requestCheck ? "60px" : "30px"} />
        <button
          className="btn btn-info"
          onClick={() => abortlogretrieve()}
          disabled={!startButton}
        >
          Abort
        </button>
        <br />
        <br />
        <hr></hr>
        <center>
          Please refrain from navigating back, refreshing, or closing the page
          once the retrieval has started. Doing so may result in the loss of
          retrieved data.
          <br />
          Retrieving process time will vary according to the data size.
          <br />
          Please be patient.
        </center>
      </div>
    </div>
  );
};
/*

      <label>
        <strong>{retrieveStatus}</strong>
      </label>
      <br />
      <br />
*/
